import React from "react";
import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  KeyboardArrowDown as DownIcon,
  KeyboardArrowUp as UpIcon,
  Search as SearchIcon,
} from "@mui/icons-material";

interface SearchBarProps {
  value: string;
  onChange: (v: string) => void;
  matchCount?: number;
  currentMatch?: number;
  onPrev?: () => void;
  onNext?: () => void;
  showMatchCount?: boolean;
  showNavigation?: boolean;
  placeholder?: string;
  minWidth?: number;
  sx?: object;
}

const SearchBar: React.FC<SearchBarProps> = ({
  value,
  onChange,
  matchCount,
  currentMatch,
  onPrev,
  onNext,
  showMatchCount = true,
  showNavigation = true,
  placeholder = "Search…",
  minWidth = 220,
  sx,
}) => {
  const hasValue = value.length > 0;
  const navigationEnabled =
    showNavigation && hasValue && matchCount !== undefined && matchCount > 0;
  const counterVisible =
    showMatchCount && hasValue && matchCount !== undefined;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        flexShrink: 0,
        flexWrap: "nowrap",
        minWidth: 0,
        ...sx,
      }}
    >
      <TextField
        size="small"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && navigationEnabled) {
            e.preventDefault();
            if (e.shiftKey) {
              onPrev?.();
            } else {
              onNext?.();
            }
          }
        }}
        placeholder={placeholder}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: hasValue ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={() => onChange("")}
                aria-label="Clear search"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        }}
        sx={{
          flex: "1 1 auto",
          minWidth: { xs: 0, sm: minWidth },
          maxWidth: "100%",
          "& .MuiInputBase-input": { py: 0.75 },
        }}
      />
      {showNavigation && hasValue && (onPrev || onNext) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            backgroundColor: "background.paper",
          }}
        >
          <Tooltip title="Previous match (Shift+Enter)">
            <span>
              <IconButton
                size="small"
                onClick={onPrev}
                disabled={!navigationEnabled}
                aria-label="Previous match"
                sx={{ borderRadius: 0 }}
              >
                <UpIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Next match (Enter)">
            <span>
              <IconButton
                size="small"
                onClick={onNext}
                disabled={!navigationEnabled}
                aria-label="Next match"
                sx={{ borderRadius: 0 }}
              >
                <DownIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}
      {counterVisible && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ whiteSpace: "nowrap", minWidth: 56, textAlign: "center" }}
        >
          {matchCount === 0
            ? "0 / 0"
            : currentMatch != null && currentMatch > 0
              ? `${currentMatch} / ${matchCount}`
              : `${matchCount} match${matchCount === 1 ? "" : "es"}`}
        </Typography>
      )}
    </Box>
  );
};

export default SearchBar;
